import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "h-14 w-full border border-white/10 bg-ink px-5 font-mono text-xs tracking-wider text-zinc-100 placeholder:text-zinc-600 transition-colors duration-300 focus:border-white/50 focus:outline-none";

export default function Account() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [name, setName] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/auth");
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      api.get("/orders/mine").then((res) => setOrders(res.data)).catch(() => setOrders([]));
    }
  }, [user]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="account-loading">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-100" />
      </div>
    );
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.patch("/auth/profile", { name });
      setUser(res.data);
      toast.success("Profil mis à jour.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Mot de passe modifié.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1700px] px-5 pb-28 pt-36 sm:px-8 lg:px-16 lg:pt-44" data-testid="account-page">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">Espace Privé</p>
        <h1 className="mt-4 font-blackletter text-6xl leading-[1] sm:text-7xl lg:text-8xl">Mon Compte</h1>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12">
        <div className="space-y-14 lg:col-span-4">
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-coal p-7" data-testid="profile-card">
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-14 w-14 rounded-full border border-white/10" />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center border border-white/10 font-blackletter text-2xl">
                    {(user.name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <div>
                  <p className="font-display text-lg font-bold uppercase tracking-wide">{user.name}</p>
                  <p className="font-mono text-[10px] text-zinc-500">{user.email}</p>
                </div>
              </div>
              <div className="mt-6 flex gap-6 border-t border-white/10 pt-5 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                <span>{user.auth_provider === "google" ? "Via Google" : "Email"}</span>
                {user.created_at && (
                  <span>Membre depuis {new Date(user.created_at).toLocaleDateString("fr-FR")}</span>
                )}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <form onSubmit={saveProfile} className="border border-white/10 bg-coal p-7" data-testid="profile-form">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Modifier le profil</p>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nom"
                required
                data-testid="profile-name-input"
                className={`${inputCls} mt-5`}
              />
              <button
                type="submit"
                disabled={saving}
                data-testid="profile-save-button"
                className="mt-4 h-12 w-full bg-white font-mono text-[10px] uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-zinc-300 disabled:opacity-60"
              >
                Enregistrer
              </button>
            </form>
          </Reveal>

          {user.auth_provider !== "google" && (
            <Reveal delay={0.2}>
              <form onSubmit={changePassword} className="border border-white/10 bg-coal p-7" data-testid="password-form">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  Changer le mot de passe
                </p>
                <div className="mt-5 space-y-4">
                  <input
                    type="password"
                    value={pw.current}
                    onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                    placeholder="Mot de passe actuel"
                    required
                    data-testid="password-current-input"
                    className={inputCls}
                  />
                  <input
                    type="password"
                    value={pw.next}
                    onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                    placeholder="Nouveau (8+ caractères)"
                    required
                    minLength={8}
                    data-testid="password-new-input"
                    className={inputCls}
                  />
                  <input
                    type="password"
                    value={pw.confirm}
                    onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                    placeholder="Confirmer le nouveau"
                    required
                    minLength={8}
                    data-testid="password-confirm-input"
                    className={inputCls}
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="password-save-button"
                  className="mt-4 h-12 w-full border border-white/20 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-100 transition-colors duration-300 hover:border-white disabled:opacity-60"
                >
                  Modifier
                </button>
              </form>
            </Reveal>
          )}
        </div>

        <Reveal delay={0.15} className="lg:col-span-8">
          <div className="border-t border-white/10 pt-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Historique des commandes
            </p>
            {orders === null ? (
              <p className="mt-8 font-mono text-xs text-zinc-600">Chargement…</p>
            ) : orders.length === 0 ? (
              <p className="mt-8 font-serif text-2xl italic text-zinc-400" data-testid="account-orders-empty">
                Aucune commande. La collection vous attend.
              </p>
            ) : (
              <ul className="mt-8 space-y-4" data-testid="account-orders-list">
                {orders.map((o) => (
                  <li
                    key={o.id}
                    data-testid={`account-order-${o.id.slice(0, 8)}`}
                    className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-coal p-6"
                  >
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs tracking-wider text-zinc-200">
                          #{o.id.slice(0, 8).toUpperCase()}
                        </span>
                        <span
                          className={`px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] ${
                            o.status === "paid" ? "bg-white text-ink" : "border border-white/20 text-zinc-400"
                          }`}
                        >
                          {o.status === "paid" ? "Payée" : "En attente"}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">
                          {o.provider || "stripe"}
                        </span>
                      </div>
                      <p className="mt-3 font-mono text-[10px] text-zinc-500">
                        {o.items?.map((i) => `${i.name} (${i.size}) ×${i.quantity}`).join(" · ")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl font-bold">€{o.total?.toFixed(0)}</p>
                      <p className="mt-1 font-mono text-[9px] text-zinc-600">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
